<template>
  <div class="pax-widget" :id="'paxwidget-' + this.id">
    <div class="position-relative">
      <div class="row mx-0">
        <div class="pax-card">
          <div class="added-pax">
            <span class="min-pax">{{ min }}</span>
            <span class="durchfuehr-min-pax">{{ drfMinPax }}</span>
          </div>
          <div class="added-pax">
            <span class="kalk-pax">{{ kalk || 0 }}</span>
            <span class="forecast-pax">{{ fcPax }}</span>
          </div>
        </div>

        <div class="ist-pax" :class="istBgColor">
          <div class="ist-pax-text">
            {{ ist }}
          </div>
          <i v-if="istBgColor === 'greenalert'" class="flaticon2-exclamation ist-pax-alert"></i>
          <span class="plus-sign"><Plus :size="8" :stroke-width="3" /></span>
        </div>

        <div class="pax-card">
          <div class="added-pax">
            <span class="opt-pax" :class="optBgColor">{{ opt }}</span>
            <span class="anf-pax" :class="anfBgColor">{{ anf }}</span>
          </div>
          <div class="pax-num">{{ max }}</div>
        </div>
      </div>
      <b-tooltip boundary-padding="0" :target="'paxwidget-' + this.id" placement="bottom">
        <div class="d-flex">
          <div class="d-flex flex-wrap">
            <span>Min</span>
            <span>DMIN</span>
          </div>
          <div class="d-flex flex-wrap">
            <span>KALK</span>
            <span>FC</span>
          </div>
          <div>{{ $t('PAX.IST') }}</div>

          <div class="d-flex flex-wrap pl-3">
            <span>{{ $t('PAX.OPT') }}</span>
            <span>{{ $t('PAX.ANF') }}</span>
          </div>
          <div class="">{{ $t('PAX.MAX') }}</div>
        </div>
      </b-tooltip>
    </div>
  </div>
</template>

<script>
import { Plus } from 'lucide-vue';

export default {
  name: 'PaxWidget',
  components: {
    Plus,
  },
  data() {
    return {
      isHover: false,
      mouseOverLast: false,
      mouseOverDelay: 800,
      mouseOverTimeout: null,
    };
  },
  props: {
    paxObject: {
      type: Object,
    },
    minPax: {
      type: Number,
    },
    maxPax: {
      type: Number,
    },
    kalkPax: {
      type: Number,
    },
    forecastPax: {
      type: Number,
    },
    durchfuehrbareMinPax: {
      type: Number,
    },
    id: {
      type: Number,
    },
  },
  computed: {
    ist() {
      return this.paxObject?.ist || 0;
    },
    min() {
      return this.minPax || 0;
    },
    max() {
      return this.maxPax || 0;
    },
    kalk() {
      return this.kalkPax || 0;
    },
    opt() {
      return this.paxObject?.opt || 0;
    },
    anf() {
      return this.paxObject?.anf || 0;
    },
    war() {
      return this.paxObject?.war || 0;
    },
    vor() {
      return this.paxObject?.vor || 0;
    },
    fcPax() {
      return this.forecastPax || 0;
    },
    drfMinPax() {
      return this.durchfuehrbareMinPax || 0;
    },
    istBgColor() {
      return this.ist === 0
        ? 'gray'
        : this.ist < this.min && this.ist !== 0
        ? 'red'
        : this.ist >= this.min && this.ist < this.kalk
        ? 'yellow'
        : this.ist >= this.kalk && this.ist < this.max
        ? 'green'
        : this.ist === this.max
        ? 'darkgreen'
        : 'greenalert';
    },

    optBgColor() {
      return this.getBgColor(this.opt);
    },
    anfBgColor() {
      return this.getBgColor(this.anf);
    },
  },
  methods: {
    getBgColor(field) {
      if (this.istBgColor === 'red' && field > 0) {
        return 'light-red';
      } else if (this.istBgColor === 'yellow' && field > 0) {
        return 'light-yellow';
      } else if (this.istBgColor === 'green' && field > 0) {
        return 'light-green';
      } else {
        return 'light-grey';
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';

.ist-pax.red {
  background-color: rgba($danger, 0.5);
  border: 0.5px solid $danger;
}
.ist-pax.yellow {
  background-color: rgba($warning, 0.5);
  border: 0.5px solid $warning;
}
.ist-pax.green,
.ist-pax.greenalert {
  background-color: rgba($success, 0.5);
  border: 0.5px solid $success;
}
.ist-pax.darkgreen {
  background-color: rgba(#159892, 0.5);
  border: 0.5px solid #159892;
}
.ist-pax.gray {
  background-color: rgba($gray-500, 0.5);
  border: 0.5px solid $gray-500;
}

.opt-pax.light-red,
.anf-pax.light-red {
  background-color: $danger-light;
}
.opt-pax.light-yellow,
.anf-pax.light-yellow {
  background-color: $warning-light;
}
.opt-pax.light-grey,
.anf-pax.light-grey {
  background-color: $secondary;
}

.pax-widget {
  display: flex;
  align-items: center;
  text-align: center;
  width: 140px;
  height: 35px;
  white-space: nowrap;
}

.row.mx-0 {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
}

.pax-card {
  display: flex;
  gap: 1px;
  background-color: $secondary;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0px 1px 2px 0px #0000001a;
}

.added-pax {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.min-pax,
.durchfuehr-min-pax,
.kalk-pax,
.forecast-pax,
.opt-pax,
.anf-pax {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 17px;
  background-color: $secondary;
  box-shadow: 0px 1px 2px 0px #0000001a;
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
}

.ist-pax {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 35px;
  margin: 0 -5px;
  border-radius: 5px;
  box-shadow: 0px 1px 3px 0px #0000001a;
  box-sizing: border-box;
  z-index: 2;
}

.ist-pax-text {
  display: inline-block;
  font-size: 18px;
  font-weight: 400;
}

.ist-pax-alert {
  color: $danger;
  font-size: 1.35rem;
}

.plus-sign {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  height: 10px;
  width: 10px;
  border-radius: 10px;
  background-color: var(--surface);
  color: #000000;
  box-shadow: 0px 1px 2px 0px #0000001a;
  z-index: 3;
}

// max-Zelle: volle Kartenhöhe, eckig
.pax-num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 35px;
  background-color: $secondary;
  box-shadow: 0px 1px 2px 0px #0000001a;
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
}

.tooltip {
  opacity: 1 !important;
}
</style>
